import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useOrganization } from "../contexts/OrganizationContext";
import { searchAll, detectQueryIntent, type QueryIntent } from "../lib/typesense-service";
import { getScopedSearchKey, searchRoleGated } from "../lib/search/typesenseScopedSearch";
import { useDebounce } from "./useDebounce";
import { searchKeys } from "./useTypesenseSearch";
import {
  COLLECTION_META,
  MODULE_COLLECTION_PRIORITY,
  isTypesenseEnabled,
  ROLE_GATED_COLLECTIONS,
} from "../config/typesense";
import { useModule } from "../contexts/ModuleContext";
import type { ModuleId } from "../contexts/ModuleContext";
import type { MultiSearchResult } from "../types/search";
import { prioritizeResults } from "../lib/search/ranking";

// Ranking is pure and lives in lib/search/ranking.ts (the golden-set scorer imports it
// outside the browser); re-exported so existing importers keep working.
export { prioritizeResults };

/**
 * Split the accessible collections into the batch that must come back first and
 * the batch that fills in behind it.
 *
 * Typesense executes a multi_search's searches SERIALLY — `post_multi_search` in
 * the server is a plain `for` loop over the `searches` array calling
 * `CollectionManager::do_search` one at a time — so a single request covering
 * every collection costs the SUM of all of them, and nothing renders until the
 * last one finishes. An admin with access to every module was paying ~20 serial
 * searches per debounced keystroke before the first result could paint.
 *
 * Splitting the fan-out into two concurrent requests fixes both halves of that:
 * the batch the user is most likely to want is a much shorter serial chain, and
 * the two chains run on different server threads instead of end to end.
 *
 * The first batch is deliberately EXACTLY the set {@link prioritizeResults} puts
 * in its top section, and the partition rule here mirrors that function's. That
 * is what keeps progressive rendering from reshuffling results under the user's
 * cursor: whatever lands first is already in its final section and final order,
 * and the second batch only ever appends "Other results" beneath it. A test pins
 * the two partitions to agree — if they ever drift, the symptom is a visible jump.
 */
export function splitSearchWaves(
  collections: string[],
  moduleId: ModuleId | null,
  intent: QueryIntent,
): { first: string[]; second: string[] } {
  const topSection =
    intent.confidence === "high" && intent.boostedCollections.length > 0
      ? intent.boostedCollections
      : (moduleId ? MODULE_COLLECTION_PRIORITY[moduleId] : undefined) ?? [];

  const topSet = new Set(topSection);
  return {
    first: collections.filter((c) => topSet.has(c)),
    second: collections.filter((c) => !topSet.has(c)),
  };
}

/**
 * Run one batch of collections. Both waves go through here: role-gated collections
 * go to the server search, the rest to Typesense with the browser's scoped keys.
 */
export async function searchWave(
  query: string,
  orgId: string,
  collections: string[],
  perCollection: number,
  canViewCandidates: boolean,
): Promise<MultiSearchResult> {
  // #5550: role-gated collections are not on the browser key; the server searches
  // them after checking the caller's role. Everything else goes to Typesense directly.
  const gated = collections.filter((c) => ROLE_GATED_COLLECTIONS.has(c));
  const open = collections.filter((c) => !ROLE_GATED_COLLECTIONS.has(c));

  // Fail CLOSED on the role-gated side only: a refused or failed server search drops
  // those groups and never widens anything; the rest of Cmd+K still answers.
  const gatedPromise: Promise<MultiSearchResult["results"]> = gated.length > 0
    ? searchRoleGated(query, gated, perCollection).catch((err) => {
        if (process.env.NODE_ENV === "development") {
          console.error("Role-gated search failed (dropped):", err);
        }
        return [];
      })
    : Promise.resolve([]);

  const openPromise: Promise<MultiSearchResult> = open.length > 0
    ? searchOpenCollections(query, orgId, open, perCollection, canViewCandidates)
    : Promise.resolve({ results: [], totalFound: 0, searchTimeMs: 0 });

  const [openResult, gatedGroups] = await Promise.all([openPromise, gatedPromise]);

  // Merge back into the caller's collection order.
  const byCollection = new Map<string, MultiSearchResult["results"][number]>();
  for (const g of [...openResult.results, ...gatedGroups]) byCollection.set(g.collection, g);
  const results = collections
    .map((c) => byCollection.get(c))
    .filter((g): g is MultiSearchResult["results"][number] => g !== undefined && g.found > 0);
  return {
    results,
    totalFound: openResult.totalFound + gatedGroups.reduce((sum, g) => sum + g.found, 0),
    searchTimeMs: openResult.searchTimeMs,
  };
}

/**
 * The browser-key half of a wave: mint the scoped keys it needs, then search.
 * Both waves go through here, so the org- and candidate-scoping rules below hold
 * identically for each. The keys are cached module-level (and prewarmed at login
 * — see prewarmSearchKeys), so the second wave never re-pays for a mint.
 */
async function searchOpenCollections(
  query: string,
  orgId: string,
  collections: string[],
  perCollection: number,
  canViewCandidates: boolean,
): Promise<MultiSearchResult> {
  // #2404 / #4031: the per-org scoped key is the ONLY key the browser has, so a
  // mint failure fails the search closed (an error the palette shows) — there is
  // no broad-key fallback any more. Contacts is not on it (#3228): it is role-gated
  // and searched by the server, which withholds candidate records itself.
  const scopedApiKey = await getScopedSearchKey();

  return searchAll(query, orgId, {
    collections,
    perCollection,
    canViewCandidates,
    scopedApiKey,
  });
}

/**
 * Merge the two waves into the shape the palette consumes, keeping each wave to
 * the collections it currently owns.
 *
 * The filter is load-bearing, not defensive. Both queries keep
 * `placeholderData: (prev) => prev` so results stay on screen across a key
 * change, and the PARTITION itself can shift mid-typing: "555" reads as an
 * invoice-number intent, and the "555-1234" that follows reads as a phone
 * intent, which moves contacts / call_logs / qbo_customers out of the second
 * wave and into the first. At that moment the wave that has already refetched
 * holds the new partition while the other is still showing its
 * previous-partition placeholder, so the collections that moved are present in
 * BOTH results — duplicate group headers, duplicate React keys in
 * CommandPalette's `key={group.collection}`, and a double-counted totalFound.
 * (The same thing happens when currentModule goes null: the first wave becomes
 * empty and disabled, so its placeholder never refetches and would linger
 * indefinitely beside a second wave that now owns those collections.)
 *
 * The two waves are disjoint by construction, so scoping each to its own
 * partition makes a duplicate impossible. It also drops a stale group whose
 * collection has since moved to the other wave — correctly: those hits are for
 * the previous query text, and this render no longer puts that collection in
 * the section the stale group came from.
 */
export function mergeWaveResults(
  first: MultiSearchResult | undefined,
  second: MultiSearchResult | undefined,
  firstWave: readonly string[],
  secondWave: readonly string[],
): MultiSearchResult | undefined {
  if (!first && !second) return undefined;

  const firstSet = new Set(firstWave);
  const secondSet = new Set(secondWave);
  const results = [
    ...(first?.results ?? []).filter((g) => firstSet.has(g.collection)),
    ...(second?.results ?? []).filter((g) => secondSet.has(g.collection)),
  ];

  return {
    results,
    // Recomputed from the surviving groups. Summing each wave's own totalFound
    // would still count the groups filtered out above.
    totalFound: results.reduce((sum, g) => sum + g.found, 0),
    searchTimeMs: Math.max(first?.searchTimeMs ?? 0, second?.searchTimeMs ?? 0),
  };
}

/**
 * useGlobalSearch — search across all accessible Typesense collections at once.
 *
 * Filters collections by the user's module access before querying, then fans the
 * search out over two concurrent batches (see {@link splitSearchWaves}) so the
 * results the user is most likely to want are not stuck behind every other
 * collection's search. Debounces the query at 250ms to balance responsiveness and
 * API load.
 *
 * `data` merges whatever has arrived so far, so the palette renders the first
 * batch immediately and the second fills in beneath it.
 *
 * @param query - Raw search string from the user
 * @param options - Optional collection list override and per-collection result limit
 * @returns Combined multi-collection results, loading, and error state
 *
 * @example
 * const { data, isLoading } = useGlobalSearch(searchQuery, { perCollection: 3 });
 */
/**
 * True only when the answer on screen is the FINAL answer for what is typed: the debounce has
 * caught up and no wave is still in flight. "No results" may be said only then (Nick 2026-10-01:
 * from Home, Cmd+K said "No results found" for 57J while the ticket wave was still running).
 */
export function isSearchSettled(query: string, debouncedQuery: string, wavesFetching: readonly boolean[]): boolean {
  return query === debouncedQuery && !wavesFetching.some(Boolean);
}

export function useGlobalSearch(
  query: string,
  options: { collections?: string[]; perCollection?: number } = {},
): {
  data: MultiSearchResult | undefined;
  prioritized: MultiSearchResult["results"];
  rest: MultiSearchResult["results"];
  sectionLabel: string;
  isLoading: boolean;
  isFetching: boolean;
  /** No answer is still coming for the current query (see isSearchSettled). */
  isSettled: boolean;
  error: Error | null;
} {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId;
  const { hasModuleAccess, currentModule } = useModule();
  const debouncedQuery = useDebounce(query, 250);
  const typesenseEnabled = isTypesenseEnabled();
  const perCollection = options.perCollection ?? 5;

  // Recruiting candidates must not leak into global search for users without
  // P14 (Recruiting) access (#2559). Recruiters keep seeing candidates.
  const canViewCandidates = hasModuleAccess("P14");

  const accessibleCollections = useMemo(() => {
    const explicit = options.collections ?? Object.keys(COLLECTION_META);
    return explicit.filter((col) => {
      const meta = COLLECTION_META[col];
      return meta ? hasModuleAccess(meta.moduleId) : false;
    });
  }, [options.collections, hasModuleAccess]);

  const intent = useMemo(
    () => detectQueryIntent(debouncedQuery),
    [debouncedQuery],
  );

  const { first: firstWave, second: secondWave } = useMemo(
    () => splitSearchWaves(accessibleCollections, currentModule, intent),
    [accessibleCollections, currentModule, intent],
  );

  const queryable =
    typesenseEnabled && !!orgId && debouncedQuery.length >= 2;

  const firstResult = useQuery({
    queryKey: [
      ...searchKeys.all,
      "global",
      "wave1",
      debouncedQuery,
      firstWave,
      canViewCandidates,
      perCollection,
    ],
    queryFn: () =>
      searchWave(debouncedQuery, orgId!, firstWave, perCollection, canViewCandidates),
    enabled: queryable && firstWave.length > 0,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const secondResult = useQuery({
    queryKey: [
      ...searchKeys.all,
      "global",
      "wave2",
      debouncedQuery,
      secondWave,
      canViewCandidates,
      perCollection,
    ],
    queryFn: () =>
      searchWave(debouncedQuery, orgId!, secondWave, perCollection, canViewCandidates),
    enabled: queryable && secondWave.length > 0,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  // Merge whatever has landed. prioritizeResults below is the single source of
  // ordering truth and runs over the union, so a partially-arrived search is just
  // the same ordering applied to fewer groups — never a different ordering.
  const data = useMemo(
    () => mergeWaveResults(firstResult.data, secondResult.data, firstWave, secondWave),
    [firstResult.data, secondResult.data, firstWave, secondWave],
  );

  const groups = useMemo(() => {
    const hits = data?.results.filter((r) => r.hits.length > 0) ?? [];
    return prioritizeResults(hits, currentModule, intent);
  }, [data, currentModule, intent]);

  return {
    data,
    prioritized: groups.prioritized,
    rest: groups.rest,
    sectionLabel: groups.sectionLabel,
    // "Loading" is about having nothing useful on screen yet, which is the first
    // wave's job. Once it lands the palette has results, even while the second
    // wave is still in flight — that shows as isFetching (a subtle spinner), not
    // as an empty loading state.
    isLoading: firstWave.length > 0 ? firstResult.isLoading : secondResult.isLoading,
    isFetching: firstResult.isFetching || secondResult.isFetching,
    isSettled: isSearchSettled(query, debouncedQuery, [firstResult.isFetching, secondResult.isFetching]),
    error: firstResult.error ?? secondResult.error,
  };
}
